// Check against the plan --- updating a draft PO from the check (shared/po-plan-check.ts) and recording that staff went
// through it, spread into `mutations` (shared/mutations.ts). Implementations: server/src/mutations/po-plan-check.ts.
// "Send anyway" isn't a mutation of its own: po.mark_sent and the Gmail send take `plan_check` (planCheckSeen).
import { z } from "zod";
import { custom } from "./mutation-kit.ts";

/** checkKey(): the differences staff saw ('' = none). */
const checkKey = z.string().max(40);

/** What the send warning showed when staff sent anyway: recorded on the PO (plan_check_overridden, plan_check_note). */
export const planCheckSeen = z.object({ key: checkKey, note: z.string().trim().max(2000) });

export const poPlanCheckMutations = {
  /**
   * The check's Update selected (updateItems()): the ticked items, each with what to do and its lines as the browser's
   * check saw them. The server works the check out again and refuses (409) if one of those lines changed since or the
   * plan now says something else; otherwise ticked lines get the plan's quantity and a new plan_basis, ticked
   * not-needed lines are taken off, ticked additions are added (`line_id`: the new line's id, so it shows at once;
   * builds on make POs get their make orders), all in one transaction through the PO line rules. Lines changed here
   * are a person's to auto mode (auto_qty cleared). Records plan_checked_at/by and the differences left as they are.
   */
  "po.update_from_plan": custom(
    z.object({
      id: z.uuid(),
      items: z
        .array(
          z.object({
            item_id: z.uuid(),
            action: z.enum(["set", "remove", "add"]),
            lines: z
              .array(z.object({ id: z.uuid(), qty: z.number() }))
              .max(50)
              .default([]),
            line_id: z.uuid().optional(),
          }),
        )
        .min(1, "Tick at least one line")
        .max(500),
    }),
  ),
  /** "Keep as they are": staff went through the check and keep the draft's quantities (`key`: the differences they saw). */
  "po.plan_checked": custom(z.object({ id: z.uuid(), key: checkKey })),
} as const;
